/**
 * Telling the reader's sort changes apart from MUI X's own clean-up of the sort model. Pure, unit-tested in
 * `e2e/unit/gridSortModel.spec.ts`; `DataGrid.tsx` (`onSortModelChange`) uses it.
 *
 * When the grid's columns change, MUI X removes every sort item whose column it does not know at that moment
 * (`handleColumnsChange` in useGridSorting) and reports the shorter model through `onSortModelChange`. On the
 * first answer of a grid that the server sorts (the short-interest hub's grids, the screeners), that fires while
 * MUI is still rebuilding its column state: it drops the server's own sort, the grid took it for a sort by the
 * reader, and requested and drew the same first page a second time (ortex-gui#1370). A reader can only sort by,
 * or stop sorting by, a column the grid has, so a change that only removes items for unknown columns is MUI's.
 */

export interface SortItem {
    field: string;
    sort?: "asc" | "desc" | null | undefined;
}

/**
 * True when `next` is `current` minus items whose columns the grid does not know (`hasColumn` false), and nothing
 * else changed: MUI X's clean-up rather than a sort the reader chose.
 */
export const isColumnCleanupOnly = (current: readonly SortItem[] | undefined, next: readonly SortItem[] | undefined, hasColumn: (field: string) => boolean): boolean => {
    const before = current ?? [];
    const after = next ?? [];
    if (after.length >= before.length) return false;
    const same = (a: SortItem, b: SortItem) => a.field === b.field && (a.sort ?? null) === (b.sort ?? null);
    // Everything kept is unchanged and in the same order...
    let i = 0;
    for (const item of before) {
        if (i < after.length && same(item, after[i])) {
            i++;
            continue;
        }
        // ...and everything removed is for a column the grid does not have.
        if (hasColumn(item.field)) return false;
    }
    return i === after.length;
};
